import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, MapPin, Star, Calendar } from "lucide-react";

interface VideoTestimonial {
  id: string;
  title: string;
  customerName: string;
  location: string;
  service: string;
  rating: number;
  date: string;
  thumbnail: string;
  duration: string;
  highlights: string[];
}

const testimonials: VideoTestimonial[] = [
  {
    id: "arnhem-zonnepanelen-1",
    title: "Van €200 naar €50 energierekening",
    customerName: "Familie Van der Berg",
    location: "Arnhem",
    service: "Zonnepanelen",
    rating: 5,
    date: "December 2024",
    thumbnail: "/lovable-uploads/61ca03ab-7d73-4d3e-858b-4957ab9d692a.png",
    duration: "3:45",
    highlights: ["75% besparing", "Terug in 7 jaar", "Perfecte service"]
  },
  {
    id: "nijmegen-warmtepomp-1",
    title: "Warmtepomp in jaren '30 woning",
    customerName: "Dhr. Jansen",
    location: "Nijmegen",
    service: "Warmtepomp",
    rating: 5,
    date: "November 2024",
    thumbnail: "/lovable-uploads/a3621ac4-6b57-4867-8c55-ada144f1d70e.png",
    duration: "4:20",
    highlights: ["Oude woning", "60% gasreductie", "Comfortabeler"]
  },
  {
    id: "elst-laadpaal-1",
    title: "Thuisladen elektrische auto",
    customerName: "Familie Hendriks",
    location: "Elst",
    service: "Laadpaal",
    rating: 5,
    date: "Oktober 2024",
    thumbnail: "/lovable-uploads/cddd9f47-7597-4b14-92b8-32e9077ebccd.png",
    duration: "2:30",
    highlights: ["Smart charging", "Nachtief laden", "€500 subsidie"]
  },
  {
    id: "arnhem-batterij-1",
    title: "Energieonafhankelijk met thuisbatterij",
    customerName: "Familie Bakker",
    location: "Arnhem",
    service: "Thuisbatterij",
    rating: 5,
    date: "September 2024",
    thumbnail: "/lovable-uploads/581947eb-80b2-4a9d-a983-22362f5389f9.png",
    duration: "3:15",
    highlights: ["90% zelfvoorzienend", "Stroompanne-proof", "Slimme opslag"]
  },
  {
    id: "duiven-combinatie-1",
    title: "Complete duurzame transformatie",
    customerName: "Familie Smits",
    location: "Duiven",
    service: "Combinatie",
    rating: 5,
    date: "Augustus 2024",
    thumbnail: "/lovable-uploads/988a6a14-58dc-49f1-9611-dbc93b130352.png",
    duration: "5:40",
    highlights: ["Zonnepanelen + warmtepomp", "Energieneutraal", "€3000 subsidie"]
  },
  {
    id: "westervoort-renovatie-1",
    title: "Verduurzaming bij renovatie",
    customerName: "Dhr. & Mw. De Wit",
    location: "Westervoort",
    service: "Renovatie project",
    rating: 5,
    date: "Juli 2024",
    thumbnail: "/lovable-uploads/e66e3d6b-e6b3-41e3-940e-9f5680149582.png",
    duration: "4:55",
    highlights: ["Totale renovatie", "A++ label", "Waardestijging"]
  }
];

export function VideoTestimonials() {
  const [selectedLocation, setSelectedLocation] = React.useState<string>('all');
  
  const locations = ['all', 'Arnhem', 'Nijmegen', 'Elst', 'Duiven', 'Westervoort'];
  
  const filteredTestimonials = selectedLocation === 'all' 
    ? testimonials 
    : testimonials.filter(t => t.location === selectedLocation);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Klanten aan het woord</h2>
        <p className="text-muted-foreground">
          Echte verhalen van tevreden klanten in uw regio
        </p>
      </div>

      {/* Locatie filter */}
      <div className="flex flex-wrap gap-2 justify-center">
        {locations.map((location) => (
          <Button
            key={location}
            variant={selectedLocation === location ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedLocation(location)}
          >
            {location === 'all' ? 'Alle locaties' : location}
          </Button>
        ))}
      </div>

      {/* Video grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTestimonials.map((testimonial) => (
          <Card key={testimonial.id} className="overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="relative">
              <img 
                src={testimonial.thumbnail} 
                alt={testimonial.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Button size="lg" className="rounded-full w-16 h-16">
                  <Play className="w-6 h-6 ml-1" />
                </Button>
              </div>
              <Badge className="absolute top-2 right-2 bg-black/70 text-white">
                {testimonial.duration}
              </Badge>
            </div>
            
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg leading-tight">
                    {testimonial.title}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4">
                    <span>{testimonial.customerName}</span>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>{testimonial.location}</span>
                    </div>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{testimonial.service}</Badge>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  {testimonial.date}
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Highlights:</h4>
                <div className="flex flex-wrap gap-1">
                  {testimonial.highlights.map((highlight, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Button className="w-full group">
                <Play className="w-4 h-4 mr-2" />
                Bekijk Video
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Call to action */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="text-center py-8">
          <h3 className="text-xl font-bold mb-2">Uw verhaal volgt!</h3>
          <p className="text-muted-foreground mb-4">
            Wordt u onze volgende tevreden klant? Vraag een gratis advies aan.
          </p>
          <div className="flex gap-3 justify-center">
            <Button size="lg">
              Gratis Advies Aanvragen
            </Button>
            <Button variant="outline" size="lg">
              Bekijk Alle Cases
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}